import { createRouter, createWebHistory } from "vue-router";
import { useAuthStore } from "@/stores/auth";
import { useCoupleStore } from "@/stores/couple";
import { useCycleStore } from "@/stores/cycle";

const TRAIL_BLOCKED_ROUTE_NAMES = new Set([
  "checkin-current",
  "checkin",
  "thermometer",
  "agreements",
  "rituals",
]);

const routes = [
  {
    path: "/",
    name: "landing",
    component: () => import("@/pages/LandingPage.vue"),
    meta: { public: true },
  },
  {
    path: "/login",
    name: "login",
    component: () => import("@/pages/LoginPage.vue"),
    meta: { public: true, guestOnly: true },
  },
  {
    path: "/cadastro",
    name: "register",
    component: () => import("@/pages/RegisterPage.vue"),
    meta: { public: true, guestOnly: true },
  },
  {
    path: "/convite/:token",
    name: "invite",
    component: () => import("@/pages/InvitePage.vue"),
    meta: { public: true },
  },
  {
    path: "/app",
    component: () => import("@/layouts/AppShell.vue"),
    meta: { requiresAuth: true },
    children: [
      {
        path: "",
        name: "dashboard",
        component: () => import("@/pages/DashboardPage.vue"),
      },
      {
        path: "criar-casal",
        name: "create-couple",
        component: () => import("@/pages/CreateCouplePage.vue"),
        meta: { fullscreen: true },
      },
      {
        path: "convite",
        name: "couple-invite",
        component: () => import("@/pages/CoupleInvitePage.vue"),
        meta: { fullscreen: true },
      },
      {
        path: "boas-vindas",
        name: "welcome",
        component: () => import("@/pages/WelcomePage.vue"),
        meta: { fullscreen: true },
      },
      {
        path: "diagnostico",
        name: "diagnostic",
        component: () => import("@/pages/DiagnosticPage.vue"),
      },
      {
        path: "tarefas",
        name: "weekly-tasks",
        component: () => import("@/pages/WeeklyTasksPage.vue"),
      },
      {
        path: "momentos/:taskId/roleta",
        name: "roulette",
        component: () => import("@/pages/RoulettePage.vue"),
        meta: { fullscreen: true },
      },
      {
        path: "checkin",
        name: "checkin-current",
        component: () => import("@/pages/CheckinPage.vue"),
      },
      {
        path: "checkin/:id",
        name: "checkin",
        component: () => import("@/pages/CheckinPage.vue"),
      },
      {
        path: "termometro",
        name: "thermometer",
        component: () => import("@/pages/ThermometerPage.vue"),
      },
      {
        path: "resumo-semanal/:id",
        name: "weekly-summary",
        component: () => import("@/pages/WeeklySummaryPage.vue"),
      },
      {
        path: "combinados",
        name: "agreements",
        component: () => import("@/pages/AgreementsPage.vue"),
      },
      {
        path: "rituais",
        name: "rituals",
        component: () => import("@/pages/RitualsPage.vue"),
      },
      {
        path: "conversa-dificil",
        name: "difficult-conversation",
        component: () => import("@/pages/DifficultConversationPage.vue"),
      },
      {
        path: "historico",
        name: "history",
        component: () => import("@/pages/HistoryPage.vue"),
      },
      {
        path: "mapa-do-mes/:cycleId",
        name: "monthly-map",
        component: () => import("@/pages/MonthlyMapPage.vue"),
      },
      {
        path: "configuracoes",
        name: "settings",
        component: () => import("@/pages/SettingsPage.vue"),
      },
    ],
  },
];

const router = createRouter({
  history: createWebHistory(),
  routes,
});

router.beforeEach(async (to, from, next) => {
  const authStore = useAuthStore();

  if (!authStore.initialized) {
    await authStore.initialize();
  }

  const isAuthenticated = authStore.isAuthenticated;
  const requiresAuth = to.matched.some((record) => record.meta.requiresAuth);
  const guestOnly = to.meta.guestOnly;

  if (requiresAuth && !isAuthenticated) {
    next({ name: "login", query: { redirect: to.fullPath } });
  } else if (guestOnly && isAuthenticated) {
    next({ name: "dashboard" });
  } else if (requiresAuth && isAuthenticated && TRAIL_BLOCKED_ROUTE_NAMES.has(to.name)) {
    const coupleStore = useCoupleStore();
    const cycleStore = useCycleStore();

    if (!coupleStore.couple) {
      await coupleStore.fetchCurrent();
    }

    if (coupleStore.bothDiagnosed) {
      await cycleStore.ensureCurrent();

      if (cycleStore.trailNavLocked) {
        next(
          cycleStore.currentCheckin
            ? { name: "weekly-tasks" }
            : { name: "dashboard" }
        );
        return;
      }
    }

    next();
  } else {
    next();
  }
});

export default router;
